/**
 * The rest countdown with the screen locked (`D283`, roadmap 35a), from the
 * shell's own `RestTimer` plugin
 * (`android/app/src/main/java/app/mypocketgym/dev/RestTimerPlugin.java`).
 *
 * One notification whose countdown the system draws, with "Concluir série"
 * and "Pular descanso". It is the companion of the rest's END alarm
 * (`notifications.ts`), not its replacement: the alarm still rings when the
 * rest is over, and this one removes itself at that moment.
 *
 * ── AN OLDER SHELL ─────────────────────────────────────────────────────────
 * A bundle only reaches a shell whose build is at or above its
 * `minNativeBuild` (`D166`), but the reverse is the case that happens: the
 * updater's own fallback, or a live-reload page on an older APK. So every
 * function asks {@link restTimerAvailable} first and, without the plugin,
 * does nothing — the rest then behaves exactly as before this file.
 *
 * Web: unavailable, every function a no-op.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { isNative, toUnsubscribe, type Unsubscribe } from "./platform";

export interface RestCountdownRequest {
  readonly body: string;
  /** The low-importance channel's name in Android's settings, localised. */
  readonly channelName: string;
  readonly completeLabel: string;
  /** The rest's end, epoch milliseconds — what the countdown counts to. */
  readonly endsAt: number;
  readonly skipLabel: string;
  readonly title: string;
}

export type RestTimerAction = "complete" | "skip";

interface RestTimerPlugin {
  addListener(
    event: "restAction",
    handler: (event: { action?: string; at?: number }) => void
  ): Promise<{ remove: () => Promise<void> }>;
  hide(): Promise<void>;
  show(options: RestCountdownRequest): Promise<{ shown: boolean }>;
}

const PLUGIN = "RestTimer";

let plugin: RestTimerPlugin | null = null;

function restTimer(): RestTimerPlugin {
  plugin ??= registerPlugin<RestTimerPlugin>(PLUGIN);
  return plugin;
}

/** Whether this shell carries the plugin — false on the web and in older shells. */
export function restTimerAvailable(): boolean {
  return isNative() && Capacitor.isPluginAvailable(PLUGIN);
}

/** Shows or replaces the countdown. Resolves `false` when nothing was shown. */
export async function showRestCountdown(
  request: RestCountdownRequest
): Promise<boolean> {
  if (!restTimerAvailable()) {
    return false;
  }
  try {
    const { shown } = await restTimer().show(request);
    return shown;
  } catch (error) {
    console.warn("[native] rest countdown not shown", error);
    return false;
  }
}

export async function hideRestCountdown(): Promise<void> {
  if (!restTimerAvailable()) {
    return;
  }
  try {
    await restTimer().hide();
  } catch (error) {
    console.warn("[native] rest countdown not hidden", error);
  }
}

/** Narrows the plugin's event; anything else is not an action of ours. */
export function restTimerActionOf(event: {
  action?: unknown;
}): RestTimerAction | null {
  return event.action === "complete" || event.action === "skip"
    ? event.action
    : null;
}

/**
 * Calls `listener` for each button pressed on the countdown — in the shade,
 * on the lock screen or on a watch. An action pressed while the app was dead
 * arrives here once the app opens (the plugin retains it, for ten minutes).
 */
export function onRestTimerAction(
  listener: (action: RestTimerAction) => void
): Unsubscribe {
  if (!restTimerAvailable()) {
    return () => undefined;
  }
  return toUnsubscribe(
    restTimer().addListener("restAction", (event) => {
      const action = restTimerActionOf(event);
      if (action !== null) {
        listener(action);
      }
    })
  );
}
